import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SessionAuth } from "supertokens-auth-react/recipe/session";
import Session from "supertokens-auth-react/recipe/session";
import EmailPassword from "supertokens-auth-react/recipe/emailpassword";
import {
  api,
  getSavedAddresses,
  saveAddress,
  deleteSavedAddress,
  updateSavedAddress,
  uploadAvatar,
} from "../apiClient";

const ProfilePage = () => {
  const [fullName, setFullName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [email, setEmail] = useState("");
  const [isEmailLogin, setIsEmailLogin] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedEmail, setEditedEmail] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [editingAddressIdx, setEditingAddressIdx] = useState(null);
  const [editAddress, setEditAddress] = useState({
    first_name: "", last_name: "", address1: "", city: "", state: "", zip: "", country_code: "US",
    email: "", phone: "", label: ""
  });
  const [newAddress, setNewAddress] = useState({
    first_name: "", last_name: "", address1: "", city: "", state: "", zip: "", country_code: "US",
    email: "", phone: "", label: ""
  });
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchUserInfo() {
      try {
        setLoading(true);
        const sessionExists = await Session.doesSessionExist();
        if (sessionExists) {
          // Try to get profile from backend API (handles both email/password and third-party users)
          try {
            const response = await api.get("/users/me/profile");
            if (response.data) {
              setEmail(response.data.email || "");
              setFullName(response.data.full_name || "");
              setEditedName(response.data.full_name || "");
              setEditedEmail(response.data.email || "");
              setIsEmailLogin(response.data.is_email_login || false);
              setAvatarUrl(response.data.avatar_url || "");
            }
          } catch (apiError) {
            console.warn("Could not fetch profile from API, trying session:", apiError);
            // Fallback: try to get email from EmailPassword recipe
            try {
              const userEmail = await EmailPassword.getUserEmail();
              setEmail(userEmail || "");
            } catch (emailError) {
              console.warn("Could not get email from EmailPassword:", emailError);
            }

            // Get full name from access token payload
            const accessTokenPayload = await Session.getAccessTokenPayloadSecurely();
            const nameFromSession = accessTokenPayload.FullName || "";
            setFullName(nameFromSession);
            setEditedName(nameFromSession);
          }
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
        setMessage({ type: "error", text: "Failed to load profile information." });
      } finally {
        setLoading(false);
      }
    }

    fetchUserInfo();
  }, []);

  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const data = await getSavedAddresses();
        setSavedAddresses(data.addresses || []);
      } catch (err) {
        console.warn("Could not fetch saved addresses:", err);
      }
    };
    fetchAddresses();
  }, []);

  const handleSave = async () => {
    // Validate name
    if (!editedName.trim()) {
      setMessage({ type: "error", text: "Full name cannot be empty." });
      return;
    }

    // Validate email if editing
    if (isEditingEmail && !isEmailLogin) {
      if (!editedEmail.trim()) {
        setMessage({ type: "error", text: "Email cannot be empty." });
        return;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editedEmail.trim())) {
        setMessage({ type: "error", text: "Please enter a valid email address." });
        return;
      }
    }

    try {
      setSaving(true);
      setMessage({ type: "", text: "" });

      // Update profile via backend API
      const updateData = {
        full_name: editedName.trim(),
      };
      
      // Only include email if editing and it's not the login email
      if (isEditingEmail && !isEmailLogin) {
        updateData.email = editedEmail.trim();
      }

      const response = await api.put("/users/me/profile", updateData);

      if (response.status === 200) {
        setFullName(editedName.trim());
        if (isEditingEmail && !isEmailLogin) {
          setEmail(editedEmail.trim());
        }
        setIsEditingName(false);
        setIsEditingEmail(false);
        setMessage({ type: "success", text: "Profile updated successfully!" });
        
        // Refresh session to get updated name in token
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.detail || "Failed to update profile. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedName(fullName);
    setEditedEmail(email);
    setIsEditingName(false);
    setIsEditingEmail(false);
    setMessage({ type: "", text: "" });
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!newAddress.first_name?.trim() || !newAddress.last_name?.trim() || !newAddress.address1?.trim() ||
        !newAddress.city?.trim() || !newAddress.state?.trim() || !newAddress.zip?.trim() || !newAddress.country_code?.trim()) {
      setMessage({ type: "error", text: "Please fill in all required address fields." });
      return;
    }
    try {
      const data = await saveAddress(newAddress);
      setSavedAddresses(data.addresses || []);
      setNewAddress({ first_name: "", last_name: "", address1: "", city: "", state: "", zip: "", country_code: "US", email: "", phone: "", label: "" });
      setShowAddAddress(false);
      setMessage({ type: "success", text: "Address saved successfully!" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to save address." });
    }
  };

  const handleDeleteAddress = async (index) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await deleteSavedAddress(index);
      setSavedAddresses((prev) => prev.filter((_, i) => i !== parseInt(index, 10)));
      setMessage({ type: "success", text: "Address deleted." });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to delete address." });
    }
  };

  const handleEditAddress = (idx) => {
    const addr = savedAddresses[idx];
    if (addr) {
      setEditingAddressIdx(idx);
      setEditAddress({
        first_name: addr.first_name || "",
        last_name: addr.last_name || "",
        address1: addr.address1 || "",
        city: addr.city || "",
        state: addr.state || "",
        zip: addr.zip || "",
        country_code: addr.country_code || "US",
        email: addr.email || "",
        phone: addr.phone || "",
        label: addr.label || ""
      });
      setShowAddAddress(false);
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    if (editingAddressIdx == null) return;
    if (!editAddress.first_name?.trim() || !editAddress.last_name?.trim() || !editAddress.address1?.trim() ||
        !editAddress.city?.trim() || !editAddress.state?.trim() || !editAddress.zip?.trim() || !editAddress.country_code?.trim()) {
      setMessage({ type: "error", text: "Please fill in all required address fields." });
      return;
    }
    try {
      const data = await updateSavedAddress(editingAddressIdx, editAddress);
      setSavedAddresses(data.addresses || []);
      setEditingAddressIdx(null);
      setEditAddress({ first_name: "", last_name: "", address1: "", city: "", state: "", zip: "", country_code: "US", email: "", phone: "", label: "" });
      setMessage({ type: "success", text: "Address updated successfully!" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to update address." });
    }
  };

  const handleCancelEdit = () => {
    setEditingAddressIdx(null);
    setEditAddress({ first_name: "", last_name: "", address1: "", city: "", state: "", zip: "", country_code: "US", email: "", phone: "", label: "" });
  };

  const fallbackAvatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || "User")}&size=128&background=3b82f6&color=fff&bold=true`;

  const handleUploadProfilePicture = async () => {
    if (!avatarFile) {
      setMessage({ type: "error", text: "Please choose an image first." });
      return;
    }
    if (!avatarFile.type?.startsWith("image/")) {
      setMessage({ type: "error", text: "Selected file is not an image." });
      return;
    }

    try {
      setUploadingAvatar(true);
      setMessage({ type: "", text: "" });
      const updated = await uploadAvatar(avatarFile);
      setAvatarUrl(updated?.avatar_url || "");
      window.dispatchEvent(new CustomEvent("user-profile-updated"));
      setMessage({ type: "success", text: "Profile picture updated successfully!" });
      setAvatarFile(null);
      setAvatarPreviewUrl("");
    } catch (error) {
      console.error("Error uploading avatar:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.detail || "Failed to upload profile picture.",
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
            <p className="mt-1 text-sm text-gray-500">Manage your account information</p>
          </div>

          {/* Profile Content */}
          <div className="px-6 py-6">
            {/* Avatar Section */}
            <div className="flex items-center space-x-6 mb-8">
              <img
                src={avatarPreviewUrl || avatarUrl || fallbackAvatarUrl}
                alt="Profile"
                className="w-24 h-24 rounded-full border-4 border-blue-100"
              />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{fullName || "User"}</h2>
                <p className="text-gray-500">{email}</p>
              </div>
            </div>

            {/* Upload Avatar */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile picture
              </label>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
                  className="block w-full sm:w-auto text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-primary file:text-white hover:file:bg-blue-700"
                />
                <button
                  type="button"
                  onClick={handleUploadProfilePicture}
                  disabled={!avatarFile || uploadingAvatar}
                  className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingAvatar ? "Uploading..." : "Upload"}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Upload a JPG/PNG image. Your picture will be stored securely in MinIO.
              </p>
            </div>

            {/* Message Alert */}
            {message.text && (
              <div
                className={`mb-6 p-4 rounded-lg ${
                  message.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {message.text}
              </div>
            )}

            {/* Profile Information */}
            <div className="space-y-6">
              {/* Email (Editable if not login email) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                {isEditingEmail && !isEmailLogin ? (
                  <div className="space-y-3">
                    <input
                      type="email"
                      value={editedEmail}
                      onChange={(e) => setEditedEmail(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter your email address"
                      autoFocus={!isEditingName}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <input
                      type="email"
                      value={email || "Not set"}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                    {!isEmailLogin && (
                      <button
                        onClick={() => {
                          setIsEditingEmail(true);
                          setEditedEmail(email || "");
                        }}
                        className="ml-3 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      >
                        {email ? "Edit" : "Add Email"}
                      </button>
                    )}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {isEmailLogin 
                    ? "This email is used for login and cannot be changed. Contact support if you need to update your login email."
                    : "You can add or update your contact email address."}
                </p>
              </div>

              {/* Full Name (Editable) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                {isEditingName ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter your full name"
                      autoFocus={!isEditingEmail}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      value={fullName || "Not set"}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                    <button
                      onClick={() => {
                        setIsEditingName(true);
                        setEditedName(fullName || "");
                      }}
                      className="ml-3 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>

              {/* Save/Cancel buttons (shown when editing) */}
              {(isEditingName || (isEditingEmail && !isEmailLogin)) && (
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Saved Addresses */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Saved Addresses</h3>
              <p className="text-sm text-gray-500 mb-4">Manage your shipping addresses for faster checkout during mockup creation.</p>
              {savedAddresses.length > 0 && (
                <div className="space-y-3 mb-4">
                  {savedAddresses.map((addr, idx) => (
                    <div key={addr.id || idx} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      {editingAddressIdx === idx ? (
                        <form onSubmit={handleUpdateAddress} className="space-y-3">
                          <input type="text" placeholder="Label (e.g. Home)" value={editAddress.label} onChange={(e) => setEditAddress((a) => ({ ...a, label: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                          <div className="grid grid-cols-2 gap-3">
                            <input required placeholder="First name" value={editAddress.first_name} onChange={(e) => setEditAddress((a) => ({ ...a, first_name: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                            <input required placeholder="Last name" value={editAddress.last_name} onChange={(e) => setEditAddress((a) => ({ ...a, last_name: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                          </div>
                          <input required placeholder="Address line 1" value={editAddress.address1} onChange={(e) => setEditAddress((a) => ({ ...a, address1: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                          <div className="grid grid-cols-2 gap-3">
                            <input required placeholder="City" value={editAddress.city} onChange={(e) => setEditAddress((a) => ({ ...a, city: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                            <input required placeholder="State" value={editAddress.state} onChange={(e) => setEditAddress((a) => ({ ...a, state: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <input required placeholder="Zip" value={editAddress.zip} onChange={(e) => setEditAddress((a) => ({ ...a, zip: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                            <input required placeholder="Country code (e.g. US)" value={editAddress.country_code} onChange={(e) => setEditAddress((a) => ({ ...a, country_code: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                          </div>
                          <input type="email" placeholder="Email" value={editAddress.email} onChange={(e) => setEditAddress((a) => ({ ...a, email: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                          <input type="tel" placeholder="Phone" value={editAddress.phone} onChange={(e) => setEditAddress((a) => ({ ...a, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                          <div className="flex gap-2">
                            <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">Save Changes</button>
                            <button type="button" onClick={handleCancelEdit} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">Cancel</button>
                          </div>
                        </form>
                      ) : (
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900">{addr.label || `Address ${idx + 1}`}</p>
                            <p className="text-sm text-gray-600">{addr.first_name} {addr.last_name}</p>
                            <p className="text-sm text-gray-600">{addr.address1}, {addr.city}, {addr.state} {addr.zip}</p>
                            <p className="text-sm text-gray-600">{addr.country_code}</p>
                            {addr.email && <p className="text-sm text-gray-600">Email: {addr.email}</p>}
                            {addr.phone && <p className="text-sm text-gray-600">Phone: {addr.phone}</p>}
                          </div>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => handleEditAddress(idx)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Edit</button>
                            <button type="button" onClick={() => handleDeleteAddress(idx)} className="text-red-600 hover:text-red-800 text-sm font-medium">Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {showAddAddress ? (
                <form onSubmit={handleSaveAddress} className="space-y-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <input type="text" placeholder="Label (e.g. Home)" value={newAddress.label} onChange={(e) => setNewAddress((a) => ({ ...a, label: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                  <div className="grid grid-cols-2 gap-3">
                    <input required placeholder="First name" value={newAddress.first_name} onChange={(e) => setNewAddress((a) => ({ ...a, first_name: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                    <input required placeholder="Last name" value={newAddress.last_name} onChange={(e) => setNewAddress((a) => ({ ...a, last_name: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  <input required placeholder="Address line 1" value={newAddress.address1} onChange={(e) => setNewAddress((a) => ({ ...a, address1: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                  <div className="grid grid-cols-2 gap-3">
                    <input required placeholder="City" value={newAddress.city} onChange={(e) => setNewAddress((a) => ({ ...a, city: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                    <input required placeholder="State" value={newAddress.state} onChange={(e) => setNewAddress((a) => ({ ...a, state: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input required placeholder="Zip" value={newAddress.zip} onChange={(e) => setNewAddress((a) => ({ ...a, zip: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                    <input required placeholder="Country code (e.g. US)" value={newAddress.country_code} onChange={(e) => setNewAddress((a) => ({ ...a, country_code: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  <input type="email" placeholder="Email" value={newAddress.email} onChange={(e) => setNewAddress((a) => ({ ...a, email: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                  <input type="tel" placeholder="Phone" value={newAddress.phone} onChange={(e) => setNewAddress((a) => ({ ...a, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                  <div className="flex gap-2">
                    <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">Save Address</button>
                    <button type="button" onClick={() => setShowAddAddress(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">Cancel</button>
                  </div>
                </form>
              ) : (
                <button onClick={() => setShowAddAddress(true)} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-blue-700">Add New Address</button>
              )}
            </div>

            {/* Actions */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex space-x-4">
                <button
                  onClick={() => navigate("/orders")}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  View Order History
                </button>
                <button
                  onClick={() => navigate("/design")}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Create Design
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
